import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { isDefined } from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { updateCosimaOption } from '../../../store/actions/functional-elements.actions';
import { FormsState } from '../../../store/state/pace-forms.state';
import { Choice, Choices } from '../../../types/choice';
import { FormsConstants } from '../../../types/forms-constants';
import { CosimaOption, Option } from '../../../types/option';
import { BaseOptionComponent } from '../base-option.component';

@Component({
    selector: 'pace-forms-cosima-option',
    templateUrl: './cosima-option.component.html',
    styleUrls: ['./cosima-option.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class CosimaOptionComponent extends BaseOptionComponent implements OnInit, OnDestroy {
    public choices$: Observable<Choices>;
    public selectedChoice$: Observable<Choice | undefined>;
    declare public optionFunctionalElement$: Observable<CosimaOption>;
    public constructor(
        @Inject(LogInstanceToken) protected readonly log: LogInterface,
        protected readonly store: Store<FormsState>,
        protected readonly formBuilder: FormBuilder,
    ) {
        super(log, store, formBuilder);
    }

    public ngOnInit(): void {
        super.ngOnInit();
        this.choices$ = this.optionFunctionalElement$.pipe(
            map((o) => o.choices),
            filter(isDefined),
        );

        this.visible$ = combineLatest([this.optionFunctionalElement$, this.selectedParticipant$]).pipe(
            map(([option, selectedParticipant]) => {
                // The master displays everything. In a participant copy, we display only content that was filled out in
                // the current session and not loaded from XML.

                const visible =
                    option.visible &&
                    (selectedParticipant === FormsConstants.MASTER_PARTICIPANT_ID || !option.contentLoadedFromXml);

                return visible;
            }),
        );

        this.selectedChoice$ = this.choices$.pipe(
            map((choices) =>
                Object.values(choices)
                    .filter(isDefined)
                    .find((c) => c.selected),
            ),
        );

        this.optionFunctionalElement$
            .pipe(withLatestFrom(this.readOnly$, this.selectedChoice$, this.visible$), takeUntil(this.destroyTrigger$))
            .subscribe(([option, readOnly, selectedChoice, visible]) => {
                // this is the case when working with an individualized form
                if (!visible) {
                    return;
                }

                if (isDefined(selectedChoice)) {
                    this.radioInputControl.patchValue(selectedChoice.xmlId);
                } else {
                    this.radioInputControl.reset('');
                }

                this.radioInputControl[option.disabled || readOnly ? 'disable' : 'enable']({
                    emitEvent: false,
                });
            });

        this.radioInputControl.valueChanges
            .pipe(
                distinctUntilChanged(),
                withLatestFrom(this.optionFunctionalElement$),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(([value, option]: [string | null, Option]) => {
                this.store.dispatch(
                    updateCosimaOption({
                        id: option.id,

                        selectedChoiceXmlId: value === null ? undefined : value,
                    }),
                );
            });
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
    }
}
